import { useFuse } from '@vueuse/integrations/useFuse'
import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { catalogsAPI } from '@/api/client'
import { useToast } from '@/plugins/toast/toast'
import { useAuthStore } from '@/store/auth'
import type {
  DisablingMode,
  StockData,
  StockItem,
  StockItemTypes,
  StockPolicy,
  StockStateFilter
} from '@/types'

export const useStockStore = defineStore('stock', () => {
  const authStore = useAuthStore()
  const { locationId } = storeToRefs(authStore)
  const toast = useToast()
  const { t } = useI18n()

  const isLoading = ref<boolean>(true)

  const stockData = ref<StockData>({
    product: [],
    combo: [],
    modifier: [],
    modifierGroup: []
  })

  const searchQuery = ref<string>('')
  const activeStateFilter = ref<StockStateFilter>('ALL')
  const activeTypeFilter = ref<StockItemTypes | 'all'>('all')

  const fuseOptions = {
    keys: ['name'],
    threshold: 0.4,
    minMatchCharLength: 2
  }

  const productSearch = useFuse(searchQuery, () => stockData.value.product, {
    matchAllWhenSearchEmpty: true,
    fuseOptions
  })

  const comboSearch = useFuse(searchQuery, () => stockData.value.combo, {
    matchAllWhenSearchEmpty: true,
    fuseOptions
  })

  const modifierSearch = useFuse(searchQuery, () => stockData.value.modifier, {
    matchAllWhenSearchEmpty: true,
    fuseOptions
  })

  const modifierGroupSearch = useFuse(searchQuery, () => stockData.value.modifierGroup, {
    matchAllWhenSearchEmpty: true,
    fuseOptions
  })

  const filteredStockData = computed<StockData>(() => {
    const filter = (item: StockItem) => {
      return (
        (activeStateFilter.value === 'ENABLED' && item.enabled) ||
        (activeStateFilter.value === 'DISABLED' && !item.enabled) ||
        activeStateFilter.value === 'ALL'
      )
    }

    return {
      product: productSearch.results.value.map(el => el.item).filter(item => filter(item)),
      combo: comboSearch.results.value.map(el => el.item).filter(item => filter(item)),
      modifier: modifierSearch.results.value.map(el => el.item).filter(item => filter(item)),
      modifierGroup: modifierGroupSearch.results.value
        .map(el => el.item)
        .filter(item => filter(item))
    }
  })

  const hasResults = computed(() => {
    return (
      filteredStockData.value.product.length > 0 ||
      filteredStockData.value.combo.length > 0 ||
      filteredStockData.value.modifier.length > 0 ||
      filteredStockData.value.modifierGroup.length > 0
    )
  })

  function getMenuAvailability() {
    return catalogsAPI.getMenuAvailability()
  }

  function updateMenuAvailability(
    locationId: string,
    type: string,
    itemId: string,
    enabled: boolean,
    mode: DisablingMode,
    options: {
      until?: Date
      untilClosure?: boolean
    }
  ) {
    const payload: {
      enabled: boolean
      until?: string | null
      untilClosure?: boolean
    } = { enabled, until: null }
    if (mode === 'temporally') {
      payload.until = options.until && String(options.until)
      payload.untilClosure = options.untilClosure
    }
    return catalogsAPI.updateMenuAvailability({
      locationId,
      type,
      itemId,
      updateMenuAvailabilityRequest: payload
    })
  }

  function updateProductStock(
    productId: string,
    stock: number,
    refillPolicy: StockPolicy,
    refillValue: number,
    lowStockThreshold?: number,
    hideOnLowStock?: boolean
  ) {
    return catalogsAPI.updateProductStock({
      productId,
      updateProductStockRequest: {
        stock,
        refillPolicy,
        refillValue,
        lowStockThreshold,
        hideOnLowStock
      }
    })
  }

  async function fetchStock(): Promise<void> {
    isLoading.value = true
    const data = await getMenuAvailability()

    const mapItem = (item: StockItem) => ({
      name: item.name,
      shortName: item.shortName,
      id: item.id,
      stock: item.stock,
      color: item.color,
      imageId: item.imageId,
      enabled: item.enabled || false,
      refillPolicy: item.refillPolicy,
      refillValue: item.refillValue,
      lowStockThreshold: item.lowStockThreshold,
      hideOnLowStock: item.hideOnLowStock
    })

    stockData.value.product = data.products.map(product => ({
      type: 'product',
      ...mapItem(product as StockItem)
    }))
    stockData.value.combo = data.combos.map(combo => ({
      type: 'combo',
      ...mapItem(combo as StockItem)
    }))
    stockData.value.modifier = data.modifiers.map(modifier => ({
      type: 'modifier',
      ...mapItem(modifier as StockItem)
    }))
    stockData.value.modifierGroup = data.modifierGroups.map(modifierGroup => ({
      type: 'modifierGroup',
      ...mapItem(modifierGroup as StockItem)
    }))

    isLoading.value = false
  }

  async function toggleItem(
    item: StockItem,
    enabled: boolean,
    mode: DisablingMode = 'indefinite',
    options: {
      until?: Date
      untilClosure?: boolean
    } = {}
  ): Promise<void> {
    const stockItemTypeToEndpoint = {
      product: 'products',
      combo: 'combos',
      modifier: 'modifiers',
      modifierGroup: 'modifier_groups'
    }

    try {
      item.enabled = enabled
      await updateMenuAvailability(
        locationId.value!,
        stockItemTypeToEndpoint[item.type],
        item.id,
        enabled,
        mode,
        options
      )
    } catch {
      item.enabled = !enabled
      toast({
        title: t('menu-availability.error-saving-changes')
      })
    }
  }

  function getProductStock(productId: string) {
    return catalogsAPI.getProductStock({ productId })
  }

  async function updateItemStock(
    itemId: string,
    stock: number,
    refillPolicy: StockPolicy,
    refillValue: number,
    lowStockThreshold?: number,
    hideOnLowStock?: boolean
  ): Promise<void> {
    const item = filteredStockData.value.product.find(item => item.id === itemId)
    if (!item) return
    item.stock = stock
    item.refillPolicy = refillPolicy
    item.refillValue = refillValue
    item.lowStockThreshold = lowStockThreshold
    item.hideOnLowStock = hideOnLowStock
    await updateProductStock(
      itemId,
      stock,
      refillPolicy,
      refillValue,
      lowStockThreshold,
      hideOnLowStock
    )
  }

  async function removeItemStock(itemId: string): Promise<void> {
    const item = filteredStockData.value.product.find(item => item.id === itemId)
    if (!item) return
    item.stock = undefined
    try {
      await catalogsAPI.deleteProductStock({ productId: itemId })
    } catch {
      //no-op
    }
  }

  return {
    isLoading,
    hasResults,
    stockData,
    searchQuery,
    activeStateFilter,
    activeTypeFilter,
    filteredStockData,
    fetchStock,
    toggleItem,
    getProductStock,
    updateItemStock,
    removeItemStock
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useStockStore, import.meta.hot))
}
